<script setup lang="ts">
import { type Achievement, AchievementType } from '@/types/achievements'
import AchievementsCardSlider from '@/components/AchievementsCardsSlider.vue'
import { achievementCategories, AchievementCategory } from '@/composables/game/achievements'
import { formatUnshortenedNumber } from '../helpers/utils'
import InfoCircleIcon from '@/components/icons/InfoCircleIcon.vue'
import { Tooltip as VTooltip } from 'floating-vue'

const props = defineProps<{
	playerAchievements: Achievement
	categoryGroups: Record<AchievementCategory, {
		type: AchievementType
		data: typeof achievementCategories[AchievementType]
	}[]>
	categoryTitleMap: Record<AchievementCategory, string>
}>()

function getCurrentAchievement(categoryId: AchievementType) {
  if (!(categoryId in props.playerAchievements)) return 0
  return props.playerAchievements[categoryId]
}
</script>

<template>
  <div class="w-full flex flex-col gap-y-8">
    <div
      v-for="(categoryGroup, categoryKey) in categoryGroups"
      class="flex flex-col gap-y-1"
      :key="categoryKey"
    >
      <div class="text-white text-lg font-semibold">
        {{ categoryTitleMap[categoryKey] }}
      </div>
      <div
        class="flex flex-col mb-3 last:mb-0"
        v-for="(achievement, index) in categoryGroup.filter(a => a.type !== AchievementType.CardsAbove150)"
        :key="index"
      >
        <div class="text-light-white/70 text-base font-semibold -tracking-[0.32px] flex justify-between">
          <div class="flex items-center gap-x-1 min-w-0">
            <div class="truncate">
              {{ achievement.data.title }}
            </div>
            <VTooltip
              v-if="achievement.data.titleTooltipComponent"
              placement="right"
              distance="4"
              popper-class="achievement-card-tooltip"
            >
              <InfoCircleIcon class="size-4 min-w-4 text-white/[38%]" />
              <template #popper>
                <span
                  v-if="typeof achievement.data.titleTooltipComponent === 'string'"
                >{{ achievement.data.titleTooltipComponent }}</span>
                <component
                  v-else
                  :is="achievement.data.titleTooltipComponent"
                />
              </template>
            </VTooltip>
          </div>
          <div class="pt-0.5 text-sm font-medium">
            Current: {{ formatUnshortenedNumber(getCurrentAchievement(achievement.type)) }}
          </div>
        </div>
        <AchievementsCardSlider
          :category="achievement.type"
          :cards="achievement.data.milestones"
          :max-levels="achievement.data.maxLevels"
          :current-value="getCurrentAchievement(achievement.type)"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.achievement-card-tooltip {
	.v-popper__inner {
		@apply text-left whitespace-pre max-w-[158px] #{!important};
	}
}
</style>